import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    IconArrowLeft, IconPhone, IconMapPin, IconFileInvoice,
    IconDownload, IconPencil, IconTrash, IconPlus, IconReceipt2, IconCoin,
    IconAlertCircle, IconAlertTriangle,
} from '@tabler/icons-react';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import sohozJobCustomerService from '../../../services/sohozJobCustomerService';
import invoiceService from '../../../services/invoiceService';
import {
    buildPaginatedInvoiceHTML, DEFAULT_INVOICE_TEMPLATE,
    invoiceDeposit, invoicePaidAmount, isCustomerInvoice, customerPreviousDue,
} from '../../../lib/invoiceTemplates';
import { notifications } from '@/lib/notifications';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

// ── Bengali digit + money helpers (mirrors InvoicePage) ──────────────────────
const BN = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const toBn = (s) => String(s).replace(/[0-9]/g, (d) => BN[d]);
const fmtAmount = (num) => {
    const n = Number(num) || 0;
    return toBn(n.toLocaleString('en-BD', { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
};
const getBnDate = (d = new Date()) =>
    new Date(d).toLocaleDateString('bn-BD', { year: 'numeric', month: 'long', day: 'numeric' });

const custName = (c) => c?.applicant_name_bn || c?.applicant_name_en || '—';

// Print/save an HTML string via a throwaway hidden iframe.
function printHtml(html) {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 1000);
    };
}

export default function CustomerDetailsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const { user } = useAuthStore();
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);
    const shopInfo = {
        shopName: selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName,
        businessAddress: selectedOfficeSpace?.businessProfile?.businessAddress || user?.businessAddress,
        phone: selectedOfficeSpace?.businessProfile?.shopPhone || user?.phone,
        // Shop's own contact email — no personal-email fallback (omitted when unset).
        email: selectedOfficeSpace?.businessProfile?.shopEmail || '',
    };
    const selectedTemplate = user?.settings?.invoiceTemplate || DEFAULT_INVOICE_TEMPLATE;

    const [customer, setCustomer] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        Promise.all([
            sohozJobCustomerService.get(id).then((res) => res.data).catch(() => null),
            invoiceService.list({ limit: 500 }).then((res) => res.data ?? []).catch(() => []),
        ])
            .then(([cust, invs]) => {
                if (!alive) return;
                setCustomer(cust);
                setInvoices(invs);
            })
            .catch((err) => notifications.show({ message: err.message || t('common.error'), color: 'red' }))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [id]);

    // Invoices for this customer: match by linked customerId, or fall back to a
    // matching phone number (covers invoices typed in manually without linking).
    const customerInvoices = useMemo(
        () => invoices.filter((inv) => isCustomerInvoice(inv, id, customer?.mobile_number)),
        [invoices, id, customer]
    );

    const stats = useMemo(() => {
        const billed = customerInvoices.reduce((s, inv) => s + (Number(inv.grandTotal) || 0), 0);
        const paid = customerInvoices.reduce((s, inv) => s + invoicePaidAmount(inv), 0);
        return { count: customerInvoices.length, billed, paid, due: Math.max(0, billed - paid) };
    }, [customerInvoices]);

    // Paginated build so long invoices print as real pages (repeated header,
    // totals/footer on the last page) instead of one overflowing sheet.
    const downloadInvoice = async (inv) => printHtml(await buildPaginatedInvoiceHTML(selectedTemplate, {
        shop: shopInfo,
        customerName: inv.customerName,
        customerPhone: inv.customerPhone,
        invoiceNumber: inv.invoiceNumber,
        invoiceDate: getBnDate(inv.createdAt),
        items: Array.isArray(inv.items) ? inv.items : [],
        discount: inv.discount,
        deposit: invoiceDeposit(inv),
        isPaid: inv.status === 'paid',
        previousDue: user?.settings?.invoiceSettings?.showPreviousDue
            ? customerPreviousDue(customerInvoices, id, customer?.mobile_number, inv.id)
            : 0,
    }, user?.settings?.invoiceSettings, { mode: 'export' }));

    const handleDelete = async () => {
        if (!confirmDeleteId) return;
        setDeleting(true);
        try {
            await invoiceService.remove(confirmDeleteId);
            setInvoices((prev) => prev.filter((i) => i.id !== confirmDeleteId));
            setConfirmDeleteId(null);
            notifications.show({ message: 'ইনভয়েস মুছে ফেলা হয়েছে', color: 'green' });
        } catch (err) {
            notifications.show({ message: err.message || 'মুছে ফেলা যায়নি', color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    const district = customer?.home_district || customer?.present_district;

    return (
        <div className="font-hind py-3 sm:py-6 space-y-5">
            {/* Back */}
            <button
                type="button"
                onClick={() => navigate('/income?tab=customers')}
                className="inline-flex items-center gap-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-primary transition-colors"
            >
                <IconArrowLeft size={16} />
                {t('incomeExpense.backToCustomers') || 'গ্রাহক তালিকায় ফিরুন'}
            </button>

            {/* Customer header card */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                        <span className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-primary/10 text-primary text-xl font-bold">
                            {custName(customer).charAt(0)}
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-lg sm:text-xl font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                                {loading ? '—' : custName(customer)}
                            </h1>
                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-zinc-500 dark:text-zinc-400">
                                {customer?.mobile_number && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <IconPhone size={14} /> {customer.mobile_number}
                                    </span>
                                )}
                                {district && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <IconMapPin size={14} /> {district}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/income?tab=invoice', { state: { invoiceCustomer: customer } })}
                        disabled={!customer}
                        className="inline-flex flex-none items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-40"
                    >
                        <IconPlus size={16} />
                        {t('incomeExpense.newInvoice') || 'নতুন ইনভয়েস'}
                    </button>
                </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard icon={IconReceipt2} tint="blue" label={t('incomeExpense.totalInvoices') || 'মোট ইনভয়েস'} value={toBn(stats.count)} />
                <StatCard icon={IconFileInvoice} tint="zinc" label={t('incomeExpense.totalBilled') || 'মোট বিল'} value={`৳${fmtAmount(stats.billed)}`} />
                <StatCard icon={IconCoin} tint="emerald" label={t('incomeExpense.totalPaid') || 'মোট পরিশোধিত'} value={`৳${fmtAmount(stats.paid)}`} />
                <StatCard icon={IconAlertCircle} tint="red" label={t('incomeExpense.totalDue') || 'মোট বকেয়া'} value={`৳${fmtAmount(stats.due)}`} />
            </div>

            {/* Invoices list */}
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-100 dark:border-zinc-800/60">
                    <IconFileInvoice size={18} className="text-zinc-500 dark:text-zinc-400" />
                    <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-200">
                        {t('incomeExpense.customerInvoices') || 'গ্রাহকের ইনভয়েস'}
                    </h3>
                    {!loading && <span className="text-xs text-zinc-400 dark:text-zinc-500">({toBn(customerInvoices.length)})</span>}
                </div>

                {loading ? (
                    <div className="py-16 flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-xs text-zinc-500">{t('common.loading')}</p>
                    </div>
                ) : customerInvoices.length === 0 ? (
                    <div className="py-16 flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <IconFileInvoice size={28} className="text-zinc-400 dark:text-zinc-600" />
                        </div>
                        <p className="text-sm text-zinc-500">{t('incomeExpense.noInvoicesForCustomer') || 'এই গ্রাহকের এখনও কোনো ইনভয়েস নেই'}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[560px] border-collapse text-sm">
                            <thead>
                                <tr className="text-left">
                                    {[
                                        t('incomeExpense.invoiceNo') || 'ইনভয়েস নং',
                                        t('incomeExpense.date') || 'তারিখ',
                                        t('incomeExpense.total') || 'সর্বমোট',
                                        t('incomeExpense.status') || 'অবস্থা',
                                        '',
                                    ].map((h, i) => (
                                        <th key={i} className={`py-3 px-4 first:pl-5 last:pr-5 text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800/60 ${i === 2 ? 'text-right' : ''}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {customerInvoices.map((inv) => (
                                    <tr key={inv.id} className="border-b border-zinc-100 dark:border-zinc-800/40 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors group">
                                        <td className="py-[13px] px-4 first:pl-5 font-semibold text-zinc-800 dark:text-zinc-100 tabular-nums whitespace-nowrap">{inv.invoiceNumber}</td>
                                        <td className="py-[13px] px-4 text-[13px] text-zinc-500 dark:text-zinc-400 whitespace-nowrap">{getBnDate(inv.createdAt)}</td>
                                        <td className="py-[13px] px-4 text-right font-bold text-zinc-800 dark:text-zinc-100 tabular-nums whitespace-nowrap">৳{fmtAmount(inv.grandTotal)}</td>
                                        <td className="py-[13px] px-4">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                                inv.status === 'paid'
                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                    : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${inv.status === 'paid' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                                {inv.status === 'paid' ? (t('incomeExpense.statusPaid') || 'পরিশোধিত') : (t('incomeExpense.statusDue') || 'বকেয়া')}
                                            </span>
                                        </td>
                                        <td className="py-[13px] px-4 last:pr-5 text-right whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadInvoice(inv)}
                                                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30 transition-colors"
                                                        >
                                                            <IconDownload size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('common.download') || 'ডাউনলোড'}</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate('/income?tab=invoice', { state: { editInvoice: inv } })}
                                                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                                                        >
                                                            <IconPencil size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('common.edit') || 'এডিট'}</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfirmDeleteId(inv.id)}
                                                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                                                        >
                                                            <IconTrash size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('common.delete') || 'ডিলিট'}</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Delete confirm */}
            <Dialog open={confirmDeleteId !== null} onOpenChange={(o) => { if (!o) setConfirmDeleteId(null); }}>
                <DialogContent className="max-w-sm p-0 overflow-hidden">
                    <DialogTitle className="sr-only">ইনভয়েস ডিলিট</DialogTitle>
                    <div className="p-6 flex flex-col items-center text-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-red-500/10 dark:bg-red-900/20 flex items-center justify-center">
                            <IconAlertTriangle size={26} className="text-red-500" />
                        </div>
                        <div>
                            <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">ইনভয়েস মুছে ফেলবেন?</p>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">এই কাজটি ফেরানো যাবে না।</p>
                        </div>
                    </div>
                    <div className="flex border-t border-zinc-100 dark:border-zinc-800">
                        <button type="button" onClick={() => setConfirmDeleteId(null)} className="flex-1 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-r border-zinc-100 dark:border-zinc-800">বাতিল</button>
                        <button type="button" onClick={handleDelete} disabled={deleting} className="flex-1 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40">{deleting ? '...' : 'ডিলিট'}</button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const TINTS = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/40 text-blue-600 dark:text-blue-400',
    zinc: 'bg-zinc-50 dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/40 text-emerald-600 dark:text-emerald-400',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/40 text-red-600 dark:text-red-400',
};

function StatCard({ icon: Icon, label, value, tint }) {
    return (
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${TINTS[tint]}`}>
            <Icon size={22} className="flex-none" />
            <div className="min-w-0">
                <p className="text-[11px] font-medium truncate">{label}</p>
                <p className="text-lg font-bold tabular-nums text-zinc-800 dark:text-zinc-100">{value}</p>
            </div>
        </div>
    );
}
